import * as React from "react";
import {cx} from "emotion";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronUp,
    faFilePdf,
    faMinus,
    faPlus,
}                        from '@fortawesome/free-solid-svg-icons';

import "./history-route.scss";

import {
    ComponentLink,
    ComponentNext,
    Link,
    makeChain,
    valueHelper as value,
}                                     from "@Common/react/cst2";
import {KeyedState}                   from "@Common/react/keyed-state";
import {RangeLoader}                  from "@Common/react/range-loader";
import {UseState}                     from "@Common/react/use-state";
import {Types}                        from "@Common/types";
import {formatDate1}                  from "@Common/ui-components/format-date";
import {DataTableD1}                  from "@Common/ui-components/heavy/common/design2/data-table/data-table-d1";
import {PAGE_SIZE_ALL}                from "@Common/ui-components/heavy/common/design2/pagination-ui/page-size/page-size";
import {formatMoney}                  from "@Common/ui-components/language/format-money";
import {MEDIA_QUERY_MD, ViewportTest} from "@Common/ui-components/viewport-test";
import {sort, unique}                 from "@Common/utils/collections";
import {parsePhpDate}                 from "@Common/utils/date/parse-php-date";
import {chain}                        from "@Common/utils/fs";
import {values}                       from "@Common/utils/objects";
import {Responsive} from "@Common/ui-components/responsive";
import {consumeContext} from "@Xpresso/loaders/consume-context";
import {PrintService}   from "@Xpresso/routes/customize/steps/done/print-service/print-service";

import {OrdersHeader}                                               from "../common/orders-header/orders-header";
import {OrdersLayout}                                               from "../common/orders-layout";
import {CartAttributeDict, CartAttributeKey, CartAttributesService} from "./cart-attributes";
import {MobileCartCard}                                             from "./mobile-cart-card";
import {OrderHistoryExpansion}                                      from "./order-history-expansion/order-history-expansion";
import {SearchForm}                                                 from "./search-form/search-form";

type Cart = Types.Apis.Payloads.Next.Item.GetHistory.Cart;

export const AccountHistoryRoute = () => (
    makeChain<JSX.Element>()
    .then("printService", ({}, next:ComponentLink<typeof PrintService>) => PrintService({next}))
    .then(({}, next) => OrdersLayout({activeTab: "history", next}))

    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("userLevels"))
    .then(...consumeContext("routerHistory"))

    .then("searchForm", ({}, next:ComponentLink<typeof SearchForm>) => SearchForm({next}))
    .then("searchKey", value(
        ({searchForm}) => chain(JSON.stringify(searchForm.getValue()),
            (serialized) => serialized === "{}" ? "" : serialized
        )
    ))
    .then("limit", ({searchKey}, next:ComponentLink<typeof KeyedState<number>>) => KeyedState({
        defaultValue: 10,
        _key: searchKey,
        next: next,
    }))
    .then("cartObject", (
        ({searchKey, limit, apis, searchForm}, next:ComponentLink<typeof RangeLoader<Types.Apis.Payloads.Next.Item.GetHistory.Response, Cart>>) => RangeLoader({
            next,
            _key: searchKey,
            range: {from: 0, to: limit.value},
            load: async (range) => apis.next.item.getHistory({searchJson: {limit: range.to - range.from, offset: range.from, search: {...searchForm.getValue()}}}),
            mapLoadAsItems: (response) => values(response?.carts) ?? [],
            mapHasMore: (response) => response?.hasMore,
        })
    ))
    .then("cartArray", value(
        ({cartObject}) => sort(cartObject.getItemsInRange().filter(Boolean), cart => parsePhpDate(cart.date_modified))
            .filter((obj, index, self) => index === self.findIndex((t) => t.cartId === obj.cartId))
            .reverse()
    ))
    .then(({cartArray, cartObject, limit, searchForm, userLevels, routerHistory}, next:Link<JSX.Element>) => (
        <div className="history-route-k2i">
            {OrdersHeader({
                search: searchForm.renderInput(),
            })}
            {
                (cartArray.length === 0 && cartObject.loading) ? (
                    <div className="loading" xd-qa-type="label" xd-qa-name="Loading">
                        Loading order history...
                    </div>
                ) : cartArray.length === 0 ? (
                    <div className="no-pending-orders">
                        <p>You have not completed any orders meeting these search criteria!</p>
                        <button
                            className="to-home"
                            xd-qa-type="button"
                            xd-qa-name="Back to the Home Page"
                            onClick={() => {
                                routerHistory.push(`/`);
                            }}
                        >
                            Back to the Home Page
                        </button>
                    </div>
                ) : !cartArray[0]?.cartId ? (
                    <div className="no-pending-orders">
                        <p>There was an error loading the orders.<br/>
                        Please go back and try again.<br/>
                        If the issue persists please contact us.</p>
                        <button
                            className="to-home"
                            xd-qa-type="button"
                            xd-qa-name="Back to the Home Page"
                            onClick={() => {
                                routerHistory.push(`/`);
                            }}
                        >
                            Back to the Home Page
                        </button>
                        {userLevels.internalUser && <p>Internal Error: {cartArray[0]}</p>}
                    </div>
                ) : (
                    <div className="table">
                        {next()}
                        {(cartObject.loading || cartObject.hasMore) && (
                            <div className="load-more">
                                <button
                                    onClick={() => limit.onChange(limit.value + 10)}
                                    xd-qa-type="button"
                                    xd-qa-name="Load More"
                                >
                                    Load More
                                    {" "}
                                    {cartObject.loading && (
                                        <i className="fa fa-spinner fa-pulse"/>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                )
            }
        </div>

    ))

    .then("cartAttributesService", ({printService}, next:ComponentLink<typeof CartAttributesService, "renderChildren">) => (
        CartAttributesService({printService, renderChildren: next})
    ))
    .then("responsive", (_, next:ComponentLink<typeof Responsive>) => (
        <Responsive {...{
            sizes: {
                md: 1023,
            },
            next,
        }}/>
    ))
    .then(({cartArray, cartAttributesService, cartObject, responsive}) => (
        responsive.lt("md") ? (
            cartArray.map(cart => <MobileCartCard key={cart.cartId} attributes={cartAttributesService.mapCartAsAttributes(cart)} cart={cart} cartObject={cartObject}/>)
            ) : (
                DesktopTable({cartArray, cartAttributesService, cartObject})
            )
        // <ViewportTest
        //     if={MEDIA_QUERY_MD}
        //     then={() => cartArray.map(
        //         cart => <MobileCartCard key={cart.cartId} attributes={cartAttributesService.mapCartAsAttributes(cart)} cart={cart}/>
        //     )}
        //     else={() => DesktopTable({cartArray, cartAttributesService})}
        // />
    ))
    .run()
);

const DesktopTable = ({cartArray, cartAttributesService: {mapCartAsAttributes}, cartObject}:{
    cartArray               : Cart[],
    cartAttributesService   : ComponentNext<typeof CartAttributesService, "renderChildren">,
    cartObject              : ComponentNext<typeof RangeLoader<Types.Apis.Payloads.Next.Item.GetHistory.Response, Cart>>
}) => (
    makeChain<JSX.Element>()
    .then("collapsedState", (_, next:ComponentLink<typeof UseState<number[]>>) => UseState({
        initValue: [],
        next,
    }))
    .then("cartsAttributes", value(() => cartArray.map(mapCartAsAttributes)))
    .then("columns", value(({cartsAttributes, collapsedState}) => makeColumnHeaders({cartArray, cartsAttributes, collapsedState})))
    .then("columnsKeys", value(({columns}) => columns.map(column => column.key)))
    .then(({cartsAttributes, collapsedState, columns, columnsKeys}) => (
        DataTableD1({
            paginationKey: "history",
            darkRows: null,
            pageSize: PAGE_SIZE_ALL,
            columns,
            rows: cartArray.map((cart, index) => ({
                key         : cart.cartId.toString(),
                columns     : makeCartCells({attributes: cartsAttributes[index], cart, collapsedState}).filter(cell => columnsKeys.includes(cell.key)),
                expansion   : OrderHistoryExpansion({cart, collapsed: !!collapsedState.value.find((p) => p === cart.cartId), cartObject}),
                xdQaName    : cart.cartId.toString()
            })),
            emptyAction: {
                label: "No orders found"
            },
        })
    ))
    .run()
);

const TABLE_COLUMN_ORDERED_KEYS = ([
    "collapse",
    "cartId",
    "branch",
    "agent",
    "itemsCount",
    "priceTotal",
    "date",
    "printReceipt",
] as Array<CartAttributeKey | "collapse">);

const TABLE_COLUMN_TEMPLATES:{[Key in CartAttributeKey | "collapse"]: {
    cell   ?: (
        Omit<Parameters<typeof DataTableD1>[0]['rows'][any]['columns'][any], "render">
        & {
            makeRenderer: (props:{
                attribute       : Key extends CartAttributeKey ? CartAttributeDict[Key] : null,
                cart            : Cart,
                collapsedState  : ComponentNext<typeof UseState<number[]>>,
            }) => (() => React.ReactNode)
        }
    ),
    header ?: (
        Omit<Parameters<typeof DataTableD1>[0]['columns'][any], "render">
        & {
            makeRenderer?: (props:{
                cartArray       : Cart[],
                collapsedState  : ComponentNext<typeof UseState<number[]>>,
            }) => (() => React.ReactNode),
        }
    ),
}} = {
    agent   : {
        cell : {
            makeRenderer    : ({attribute}) => () => attribute.value,
            domAttrs        : {
                "xd-qa-type": "label",
                "xd-qa-name": "USER",
            },
        },
        header : {
            className: "text",
            domAttrs: {
                "xd-qa-type": "label",
                "xd-qa-name": "USER",
            },
            width: "25%",
        },
    },
    branch  : {
        cell    : {
            makeRenderer    : ({attribute}) => () => attribute.value,
            domAttrs        : {
                "xd-qa-type": "label",
                "xd-qa-name": "OFFICE",
            },
        },
        header  : {
            className: "text",
            width: "25%",
            domAttrs: {
                "xd-qa-type": "label",
                "xd-qa-name": "OFFICE",
            },
        },
    },
    cartId  : {
        cell : {
            makeRenderer    : ({attribute}) => () => attribute.value,
            domAttrs        : {
                "xd-qa-type": "label",
                "xd-qa-name": "CART ID",
            },
        },
        header : {
            className: "text",
            width: "25%",
            domAttrs: {
                "xd-qa-type": "label",
                "xd-qa-name": "CART ID",
            },
        },
    },
    collapse    : {
        cell    : {
            makeRenderer : ({cart: {cartId}, collapsedState: {onChange: setCollapsed, value: collapsed}}) => () => (
                <a>
                    <FontAwesomeIcon
                        key={cartId + "Chevron"}
                        className={cx("icon", {collapsed: collapsed.find((p) => p === cartId) !== undefined})}
                        icon={faChevronUp}
                        onClick={() => setCollapsed(
                            collapsed.find((p) => p === cartId) ? (
                                collapsed.filter((p) => p !== cartId)
                            ) : (
                                [...collapsed, cartId]
                            )
                        )}
                    />
                </a>
            ),
            domAttrs: {
                "xd-qa-type": "icon",
                "xd-qa-name": "Collapse",
            },
        },
        header : {
            makeRenderer : ({cartArray, collapsedState: {onChange: setCollapsed, value: collapsed}}) => (
                () => ((cartArray.length !== collapsed.length) ? (
                    <FontAwesomeIcon key={"header faMinus"} className="icon" icon={faMinus} onClick={() => (
                        setCollapsed(cartArray.map((cart) => cart.cartId))
                    )}/>
                ) : (
                    <FontAwesomeIcon key={"header faPlus"} className="icon" icon={faPlus} onClick={() =>
                        setCollapsed([])
                    }/>
                ))
            ),
            className: "text",
            width: "20px",
            domAttrs: {
                "xd-qa-type": "icon",
                "xd-qa-name": "COLLAPSE",
            },
        }
    },
    date    : {
        cell : {
            makeRenderer    : ({attribute}) => () => formatDate1(attribute.value),
            domAttrs        : {
                "xd-qa-type": "label",
                "xd-qa-name": "CHECKOUT DATE",
            },
        },
        header : {
            className: "text",
            width: "25%",
            domAttrs: {
                "xd-qa-type": "label",
                "xd-qa-name": "CHECKOUT DATE",
            },
        },
    },
    itemsCount  : {
        cell : {
            makeRenderer    : ({attribute}) => () => attribute.value,
            domAttrs        : {
                "xd-qa-type": "label",
                "xd-qa-name": "ORDERS",
            }
        },
        header : {
            className: "text",
            width: "25%",
            domAttrs: {
                "xd-qa-type": "label",
                "xd-qa-name": "ORDERS",
            },
        },
    },
    priceTotal : {
        cell    : {
            makeRenderer    : ({attribute}) => () => formatMoney(attribute.value, {useFreeLabel:false, alwaysShowDollarSymbol:true}),
            domAttrs        : {
                "xd-qa-type": "label",
                "xd-qa-name": "PRICE",
            },
        },
        header : {
            className: "text",
            width: "25%",
            domAttrs: {
                "xd-qa-type": "label",
                "xd-qa-name": "TOTAL",
            },
        },
    },
    printReceipt    : {
        cell    : {
            makeRenderer: ({attribute, cart}) => () => (
                <a onClick={attribute.run}>
                    <FontAwesomeIcon key={cart.cartId + "PDF"} className="icon" icon={faFilePdf}/>
                </a>
            ),
            domAttrs: {
                "xd-qa-type": "icon",
                "xd-qa-name": "PDF",
            },
        },
        header : {
            className: "text",
            width: "20px",
            domAttrs: {
                "xd-qa-type": "label",
                "xd-qa-name": "RECEIPT",
            },
        },
    },
};

const makeCartCells = ({attributes, cart, collapsedState}:{
    attributes      : CartAttributeDict,
    cart            : Cart,
    collapsedState  : ComponentNext<typeof UseState<number[]>>,
}): (
    Array<Parameters<typeof DataTableD1>[0]['rows'][any]['columns'][any] & {key: CartAttributeKey | "collapse"}>
) => (
    TABLE_COLUMN_ORDERED_KEYS
    .filter(key => key === "collapse" || key in attributes)
    .map(key =>({
        ...TABLE_COLUMN_TEMPLATES[key]['cell'],
        key,
        render  : TABLE_COLUMN_TEMPLATES[key]['cell'].makeRenderer({attribute: attributes[key], cart, collapsedState}),
    }))
);

const makeColumnHeaders = ({cartArray, cartsAttributes, collapsedState}:{
    cartArray       : Cart[],
    cartsAttributes : CartAttributeDict[],
    collapsedState  : ComponentNext<typeof UseState<number[]>>,
}): (
    Array<Parameters<typeof DataTableD1>[0]['columns'][any] & {key: CartAttributeKey | "collapse"}>
) => {
    const availableKeys:CartAttributeKey[] = cartsAttributes.reduce(
        (keys, attribute) => unique([...keys, ...Object.keys(attribute)]),
        []
    );
    return (
        TABLE_COLUMN_ORDERED_KEYS
        .filter(key => key === "collapse" || availableKeys.includes(key))
        .map(key => {
            const template = TABLE_COLUMN_TEMPLATES[key]['header'];
            const label    = key !== "collapse" && cartsAttributes.find(attributes => key in attributes)[key].label;
            const render   = "makeRenderer" in template && template.makeRenderer({cartArray, collapsedState});
            return {...template, key, label, render};
        })
    );
}
